import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import {
  BASE_NODE_URL,
  MERCHANT_FEED_DELETE_NOTIFICATION,
  MERCHANT_FEED_DELETE_POST,
  MERCHANT_FEED_FOLLOWER_COUNT,
  MERCHANT_FEED_FOLLOWER_LIST,
  MERCHANT_FEED_LIST_NOTIFICATIONS,
  MERCHANT_FEED_LIST_POSTS,
  MERCHANT_FEED_NOTIFICATION_TARGETS,
  MERCHANT_FEED_SAVE_NOTIFICATION,
  MERCHANT_FEED_SAVE_POST,
  MERCHANT_FEED_SEND_NOTIFICATION,
  MERCHANT_FEED_NOTIFICATION_AUDIENCE,
} from "../../../Constants/Config";
import { useAuthDetails } from "../../../Common/cookiesHelper";

const initialState = {
  postsLoading: false,
  posts: [],
  postsError: "",

  savingPost: false,
  savePostError: "",

  notificationsLoading: false,
  notifications: [],
  notificationsError: "",
  audienceOptions: [],
  audienceLoading: false,
  audienceError: "",

  followers: [],
  followersTotal: 0,
  followersLoading: false,

  savingNotification: false,
  saveNotificationError: "",

  sendingNotification: false,
  sendNotificationError: "",

  targets: null,
  followerCount: null,
};

/**
 * These endpoints sit on the Node service behind `authenticateRequest`, which
 * reads `token_id`, `login_type` and `merchant_id` from the BODY as well as the
 * bearer header — hence spreading `otherUserData` into every payload.
 *
 * `userTypeData` is read by the caller rather than here: `useAuthDetails` is
 * named as a hook, and calling it inside a plain helper trips rules-of-hooks.
 */
const postToNode = async (endpoint, data, userTypeData) => {
  const { token, ...otherUserData } = userTypeData;
  const response = await axios.post(
    BASE_NODE_URL + endpoint,
    { ...data, ...otherUserData },
    {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

const rejectShape = (error) => ({
  message: toThunkRejectValue(error).message,
  status: toThunkRejectValue(error).status,
  data: error.response ? error.response.data : null,
  isCancelled: false,
});

// --- posts -----------------------------------------------------------------

export const fetchMerchantPosts = createAsyncThunk(
  "merchantFeed/fetchMerchantPosts",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    try {
      return await postToNode(MERCHANT_FEED_LIST_POSTS, data, userTypeData);
    } catch (error) {
      return rejectWithValue(rejectShape(error));
    }
  }
);

export const saveMerchantPost = createAsyncThunk(
  "merchantFeed/saveMerchantPost",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    try {
      return await postToNode(MERCHANT_FEED_SAVE_POST, data, userTypeData);
    } catch (error) {
      return rejectWithValue(rejectShape(error));
    }
  }
);

export const deleteMerchantPost = createAsyncThunk(
  "merchantFeed/deleteMerchantPost",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    try {
      return await postToNode(MERCHANT_FEED_DELETE_POST, data, userTypeData);
    } catch (error) {
      return rejectWithValue(rejectShape(error));
    }
  }
);

// --- notifications ---------------------------------------------------------

export const fetchMerchantNotifications = createAsyncThunk(
  "merchantFeed/fetchMerchantNotifications",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    try {
      return await postToNode(MERCHANT_FEED_LIST_NOTIFICATIONS, data, userTypeData);
    } catch (error) {
      return rejectWithValue(rejectShape(error));
    }
  }
);

/**
 * Who this store can send to, and how many devices each option really reaches.
 *
 * Replaces the follower count next to the Send button. Push now goes to a
 * store's customer base rather than its followers, and for a chain it can go to
 * the whole group — so "N followers" was answering a question nobody asked.
 */
export const fetchNotificationAudience = createAsyncThunk(
  "merchantFeed/fetchNotificationAudience",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    try {
      return await postToNode(MERCHANT_FEED_NOTIFICATION_AUDIENCE, data, userTypeData);
    } catch (error) {
      return rejectWithValue(rejectShape(error));
    }
  }
);

export const saveMerchantNotification = createAsyncThunk(
  "merchantFeed/saveMerchantNotification",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    try {
      return await postToNode(MERCHANT_FEED_SAVE_NOTIFICATION, data, userTypeData);
    } catch (error) {
      return rejectWithValue(rejectShape(error));
    }
  }
);

export const sendMerchantNotification = createAsyncThunk(
  "merchantFeed/sendMerchantNotification",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    try {
      return await postToNode(MERCHANT_FEED_SEND_NOTIFICATION, data, userTypeData);
    } catch (error) {
      return rejectWithValue(rejectShape(error));
    }
  }
);

/** Drafts only — the server refuses anything already sent. */
export const deleteMerchantNotification = createAsyncThunk(
  "merchantFeed/deleteMerchantNotification",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    try {
      return await postToNode(MERCHANT_FEED_DELETE_NOTIFICATION, data, userTypeData);
    } catch (error) {
      return rejectWithValue(rejectShape(error));
    }
  }
);

export const fetchFollowerList = createAsyncThunk(
  "merchantFeed/fetchFollowerList",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    try {
      return await postToNode(MERCHANT_FEED_FOLLOWER_LIST, data, userTypeData);
    } catch (error) {
      return rejectWithValue(rejectShape(error));
    }
  }
);

export const fetchNotificationTargets = createAsyncThunk(
  "merchantFeed/fetchNotificationTargets",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    try {
      return await postToNode(MERCHANT_FEED_NOTIFICATION_TARGETS, data, userTypeData);
    } catch (error) {
      return rejectWithValue(rejectShape(error));
    }
  }
);

export const fetchFollowerCount = createAsyncThunk(
  "merchantFeed/fetchFollowerCount",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    try {
      return await postToNode(MERCHANT_FEED_FOLLOWER_COUNT, data, userTypeData);
    } catch (error) {
      return rejectWithValue(rejectShape(error));
    }
  }
);

const merchantFeedSlice = createSlice({
  name: "merchantFeed",
  initialState,
  reducers: {
    clearMerchantFeedErrors: (state) => {
      state.postsError = "";
      state.savePostError = "";
      state.notificationsError = "";
      state.saveNotificationError = "";
      state.sendNotificationError = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMerchantPosts.pending, (state) => {
        state.postsLoading = true;
      })
      .addCase(fetchMerchantPosts.fulfilled, (state, action) => {
        state.postsLoading = false;
        state.posts = action.payload?.data || [];
        state.postsError = "";
      })
      .addCase(fetchMerchantPosts.rejected, (state, action) => {
        state.postsLoading = false;
        state.postsError = action.payload?.message || "Could not load posts.";
      })

      .addCase(saveMerchantPost.pending, (state) => {
        state.savingPost = true;
        state.savePostError = "";
      })
      .addCase(saveMerchantPost.fulfilled, (state) => {
        state.savingPost = false;
      })
      .addCase(saveMerchantPost.rejected, (state, action) => {
        state.savingPost = false;
        state.savePostError = action.payload?.message || "Could not save post.";
      })

      .addCase(fetchNotificationAudience.pending, (state) => {
        state.audienceLoading = true;
        state.audienceError = "";
      })
      .addCase(fetchNotificationAudience.fulfilled, (state, action) => {
        state.audienceLoading = false;
        state.audienceOptions = action.payload?.options || [];
        // A 200 with no options means the endpoint answered but had nothing to
        // say, which is different from it not answering. Both used to leave an
        // empty array and were indistinguishable.
        state.audienceError = action.payload?.status === true ? "" : "unavailable";
      })
      .addCase(fetchNotificationAudience.rejected, (state) => {
        state.audienceLoading = false;
        state.audienceOptions = [];
        state.audienceError = "unavailable";
      })
      .addCase(fetchMerchantNotifications.pending, (state) => {
        state.notificationsLoading = true;
      })
      .addCase(fetchMerchantNotifications.fulfilled, (state, action) => {
        state.notificationsLoading = false;
        state.notifications = action.payload?.data || [];
        state.notificationsError = "";
      })
      .addCase(fetchMerchantNotifications.rejected, (state, action) => {
        state.notificationsLoading = false;
        state.notificationsError =
          action.payload?.message || "Could not load notifications.";
      })

      .addCase(saveMerchantNotification.pending, (state) => {
        state.savingNotification = true;
        state.saveNotificationError = "";
      })
      .addCase(saveMerchantNotification.fulfilled, (state) => {
        state.savingNotification = false;
      })
      .addCase(saveMerchantNotification.rejected, (state, action) => {
        state.savingNotification = false;
        state.saveNotificationError =
          action.payload?.message || "Could not save notification.";
      })

      .addCase(sendMerchantNotification.pending, (state) => {
        state.sendingNotification = true;
        state.sendNotificationError = "";
      })
      .addCase(sendMerchantNotification.fulfilled, (state) => {
        state.sendingNotification = false;
      })
      .addCase(sendMerchantNotification.rejected, (state, action) => {
        state.sendingNotification = false;
        state.sendNotificationError =
          action.payload?.message || "Could not send notification.";
      })

      .addCase(fetchNotificationTargets.fulfilled, (state, action) => {
        state.targets = action.payload?.data || null;
      })

      .addCase(fetchFollowerList.pending, (state) => {
        state.followersLoading = true;
      })
      .addCase(fetchFollowerList.fulfilled, (state, action) => {
        state.followersLoading = false;
        state.followers = action.payload?.data ?? [];
        state.followersTotal = Number(action.payload?.total ?? 0);
      })
      .addCase(fetchFollowerList.rejected, (state) => {
        state.followersLoading = false;
        state.followers = [];
        state.followersTotal = 0;
      })

      .addCase(fetchFollowerCount.fulfilled, (state, action) => {
        state.followerCount = {
          followers: action.payload?.followers ?? 0,
          reachable: action.payload?.reachable ?? 0,
        };
      });
  },
});

export const { clearMerchantFeedErrors } = merchantFeedSlice.actions;
export default merchantFeedSlice.reducer;
